import { useEffect } from 'react';
import List from 'components/list/List';
import ListBody from 'components/list/ListBody';
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch
} from 'react-router-dom';
import {
  ROUTE_USER_QUESTIONNAIRES_ENTITY,
  ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_TESTS
} from 'routes/RouteList';
import { SessionListItemUser } from 'containers/partials/list-items/SessionListItemUser';
import { Card, CardBody } from 'components/card';
import Message from 'components/message/Message';
import MessageText from 'components/message/MessageText';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import FieldInput from 'components/input/FieldInput';
import useSessionSearch from 'hooks/utils/useSessionSearch';
import { SORT_DATA_TYPES } from 'utils/sort';
import SortableListHeader from 'components/list/SortableListHeader';
import { Panel } from 'components/panel';
import { useStore } from 'stores/Store';
import usePostMessage from 'hooks/utils/usePostMessage';

const columns = [
  {
    key: 'finishedPercentage',
    label: '',
    sortable: false,
    desc: false,
    dataType: SORT_DATA_TYPES.NUMBER,
    extraProps: { id: 'listItemStatus' } // this prop is only necessary for styles. Most of the times we don't need it
  },
  {
    key: 'name',
    label: 'Session name',
    sortable: true,
    desc: false,
    dataType: SORT_DATA_TYPES.STRING,
    extraProps: { id: 'listItemMainLabel' } // this prop is only necessary for styles. Most of the times we don't need it
  },
  {
    key: 'type',
    label: 'Type',
    sortable: false,
    extraProps: { id: '' } // Empty id === hidden column
  },
  {
    key: 'startdate',
    label: 'Date',
    default: true,
    sortable: true,
    desc: true,
    dataType: SORT_DATA_TYPES.DATE_STRING,
    extraProps: { id: 'listItemLastLabel' } // this prop is only necessary for styles. Most of the times we don't need it
  }
];

function Questionnaires({ entityId }) {
  const questionnaires = useStore(state => state.questionnaires);
  const [{ filteredItems }, setState] = useSessionSearch({
    key: 'name',
    defaultSort: columns.find(c => c.default)
  });
  const active = useRouteMatch([ROUTE_USER_QUESTIONNAIRES_ENTITY]);
  const { push } = useHistory();
  const { sessionId } = useParams();
  const { sendMessage } = usePostMessage();

  useEffect(() => {
    if (questionnaires.length > 0) {
      setState({
        items: questionnaires,
        sort: {
          key: 'startdate',
          desc: true,
          dataType: SORT_DATA_TYPES.DATE_STRING
        }
      });
    }
  }, [questionnaires]);

  useEffect(() => {
    if (filteredItems?.length > 0 && active?.isExact) {
      const now = new Date();
      const tomorrow = createDate(now, 1, 14, 0);
      const firstMonday = createDate(now, (8 - now.getDay()) % 7 || 7, 14, 0);
      let firstDate = new Date('9999-01-01');
      let bestSession;

      filteredItems.forEach(session => {
        const start = new Date(session?.startdate);
        const end = new Date(session?.enddate);
        const freq = session?.interval;
        const finishedPercentage = session?.finishedPercentage;
        if (end <= now) {
          return;
        }
        let nextStartDate;

        if (finishedPercentage === 100) {
          if (freq === 'daily' || freq === 'weekly') {
            if (start > firstMonday) {
              nextStartDate = start;
            } else {
              if (freq === 'daily') {
                nextStartDate = tomorrow;
              } else {
                nextStartDate = firstMonday;
              }
            }
          } else {
            if (start > now) {
              nextStartDate = start;
            }
          }
        } else {
          if (freq === 'daily' || freq === 'weekly') {
            if (now < createDate(now, 0, 17, 30)) {
              nextStartDate = createDate(now, 0, now.getHours() < 14 ? 14 : 18);
            } else {
              nextStartDate = tomorrow;
            }
          }
        }

        if (nextStartDate && nextStartDate < firstDate) {
          firstDate = nextStartDate;
          bestSession = session;
        }
      });

      if (firstDate !== new Date('9999-01-01')) {
        sendMessage({
          message: {
            subject: 'notification',
            notification: {
              name: bestSession?.name
            },
            date: firstDate?.toISOString() || null
          }
        });
      } else {
        sendMessage({
          message: {
            subject: 'notification',
            notification: {
              name: 'none'
            }
          }
        });
      }
    }
  }, [filteredItems, active]);

  function createDate(
    now,
    day,
    hour = now.getHours(),
    minute = now.getMinutes()
  ) {
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + day,
      hour,
      minute
    );
  }

  return (
    <Panel active={active?.isExact}>
      <div className="p-tabs__tab-header u-margin-top-medium u-padding-bottom-none">
        <FieldInput
          extraClassNames="u-margin-bottom-none"
          rounded
          type="text"
          icon="search-grey"
          iconLeft
          onChange={e => setState({ search: e.target.value })}
        />
      </div>

      {filteredItems?.length > 0 ? (
        <>
          <SortableListHeader
            refactoring
            columns={columns}
            onSort={activeSort => {
              setState({ sort: activeSort });
            }}
          />
          <List>
            <ListBody>
              {filteredItems.map(session => {
                return (
                  <SessionListItemUser
                    noMaxHeight
                    id={session.id}
                    key={session.id}
                    session={session}
                    active={sessionId === session.id}
                    onClick={() => {
                      push(
                        generatePath(
                          ROUTE_USER_QUESTIONNAIRES_ENTITY_QUESTIONNAIRE_TESTS,
                          {
                            entityId,
                            sessionId: session.id
                          }
                        )
                      );
                    }}
                  />
                );
              })}
            </ListBody>
          </List>
        </>
      ) : (
        <Card secondary>
          <CardBody extraClassNames="u-margin-bottom-none u-padding-vertical-huge">
            <Message emptyState icon="test">
              <MessageText>
                <FormattedMessage {...messages.emptySessionsDataAthlete} />
              </MessageText>
            </Message>
          </CardBody>
        </Card>
      )}
    </Panel>
  );
}

export default Questionnaires;
